import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { uniqBy, get, size } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';

// Depth-1 rows are the scorecard systems, in scorecard order. supersetId matches superset id.
const depth1SupersetIds = (scores, scorecardIds) => {
  let rows = [];
  if (Array.isArray(scores)) rows = scores;
  else if (Array.isArray(get(scores, 'data'))) rows = scores.data;
  else {
    const grouped = get(scores, 'data', scores);
    if (grouped && typeof grouped === 'object') {
      const keys = size(scorecardIds) ? scorecardIds.map(String) : Object.keys(grouped);
      rows = keys.flatMap((key) => (Array.isArray(grouped[key]) ? grouped[key] : []));
    }
  }

  const wanted = new Set((scorecardIds || []).map(String));
  const ids = [];
  rows.forEach((row) => {
    if (!row || row.depth !== 1) return;
    const scorecardId = get(row, 'scorecardId', get(row, 'scorecard_id'));
    if (wanted.size && scorecardId != null && !wanted.has(String(scorecardId))) return;
    const id = get(row, 'supersetId', get(row, 'superset_id'));
    if (id == null || ids.includes(String(id))) return;
    ids.push(String(id));
  });
  return ids;
};

export const useSupersets = (props) => {
  const { assessment, scores } = props;
  // PFM-2547: /sharehash/v1/supersets is not exposed by the backend (404), so
  // skip both fetches in a hashed share-link session. Callers fall back to the
  // supersets included on the photos themselves (see ReportAssessmentPhotos).
  const hashViewHasToken = useSelector((s) => !!s?.hashAuth?.hashToken);

  const [state, setState] = useState({
    supersets: [],
  });

  const { data } = useRetrieveData(
    { url: '/supersets' },
    {
      build: ['supersets'],
      useCache: { reload: 20 },
      waitFor: hashViewHasToken ? null : !!assessment ? undefined : null,
    }
  );

  const scards = get(assessment, 'scorecards');
  const assessmentId = get(assessment, 'id', null);
  const scorecardids = (scards || []).map((sc) => sc.id);
  const hasScorecards = !!size(scorecardids);
  // System order is fixed once created. Lock it on the first depth-1 rows and
  // ignore later score updates in this assessment.
  const lockedOrder = useRef(null);
  if (lockedOrder.current?.assessmentId !== assessmentId) {
    const ids = depth1SupersetIds(scores, scorecardids);
    lockedOrder.current = ids.length ? { assessmentId, ids } : null;
  }
  const pssvIds = lockedOrder.current?.assessmentId === assessmentId ? lockedOrder.current.ids : null;

  const getData = () => {
    if (hashViewHasToken) {
      setState((s) => ({ ...s, supersets: [] }));
      return;
    }

    const wanted = new Set(scorecardids.map((id) => String(id)));
    const scorecardIdsFor = (ss) => get(ss, 'scorecardIds', get(ss, 'scorecard_ids', []));
    let res = data || [];
    if (hasScorecards && res.some((ss) => size(scorecardIdsFor(ss)))) {
      res = res.filter((ss) => (scorecardIdsFor(ss) || []).some((id) => wanted.has(String(id))));
    }

    let supersets = uniqBy(
      res.filter((f) => f),
      'id'
    ).map((ss) => ({ ...ss, id: ss.id.toString() }));

    if (pssvIds) {
      const rank = new Map(pssvIds.map((id, i) => [id, i]));
      supersets = [...supersets].sort((a, b) => {
        const ar = rank.has(a.id) ? rank.get(a.id) : Infinity;
        const br = rank.has(b.id) ? rank.get(b.id) : Infinity;
        return ar - br;
      });
    }

    setState((s) => ({ ...s, supersets }));
  };

  useEffect(() => {
    getData();
  }, [scards, hashViewHasToken, data, assessmentId, pssvIds]);

  return state;
};
